import { createFileRoute } from '@tanstack/react-router';
import _ from 'lodash';
import React from 'react';
import type * as TF from 'type-fest';
import TablerIconBrandGitHub from '~icons/tabler/brand-github';
import TablerIconBrandLinkedin from '~icons/tabler/brand-linkedin';
import TablerIconHeartHandshake from '~icons/tabler/heart-handshake';

import { Avatar, Divider, Fade, Grid, IconButton, Link, Stack, Typography } from 'gooey';
import { GlowCard } from '#/components/design';
import { Img } from '#/components/images';
import { ButtonLink } from '#/components/links';
import { Page, PageSection } from '#/components/pages';
import fx from '#/fx';
import { Content, pageSEO } from '#/lib/content';
import { canonicalLinkTag } from '#/lib/head';

export const Route = createFileRoute('/_main/about')({
  loader: async () => {
    const page = await load();
    return { page };
  },
  head: ({ loaderData, match }) => ({
    meta: pageSEO(
      {
        title: 'About',
        description:
          'At Joggr, we are a team of devs building a documentation platform purpose built for modern software development.',
        keywords: ['about', 'team', 'investors', 'careers'],
      },
      match,
      loaderData?.page
    ),
    links: [canonicalLinkTag(match.pathname)],
  }),
  component: AboutPage,
});

async function load() {
  const page = await fx.getPageContent({
    data: { key: 'about' },
  });
  const investors = await fx.listCompanyInvestors();
  const employees = await fx.listCompanyFounders();
  return {
    ...page,
    sections: {
      ...page.sections,
      investors: {
        ...page.sections.investors,
        people: _.chain(page.sections.investors.people)
          .map(({ id, order }) => {
            const found = _.find(investors, (investor) => investor.id === id);
            return found ? { ...found, id, order } : null;
          })
          .compact()
          .value(),
      },
      team: {
        ...page.sections.team,
        members: _.chain(page.sections.team.members)
          .map(({ id, order }) => {
            const found = _.find(employees, (employee) => employee.id === id);
            return found ? { ...found, id, order } : null;
          })
          .compact()
          .value(),
      },
    },
  };
}

type LoadResult = Awaited<ReturnType<typeof load>>;

function AboutPage() {
  const { page } = Route.useLoaderData();
  return (
    <Page>
      <PageSectionHero sections={page.sections} />
      <PageSectionStory sections={page.sections} />
      <PageSectionFounders sections={page.sections} />
      <PageSectionInvestors sections={page.sections} />
      <PageSectionCareers sections={page.sections} />
    </Page>
  );
}

function PageSectionHero({ sections }: AboutPageSectionProps) {
  return (
    <PageSection className='overflow-hidden z-10 py-12 md:py-36' disableGutters>
      <Stack spacing={4} justifyContent='center' alignItems='center' className='z-10 relative'>
        <Stack spacing={4} justifyContent='center' alignItems='center'>
          <Typography variant='h1' align='center'>
            <Content content={sections.hero.title} />
          </Typography>
          <Typography variant='subtitle1' align='center'>
            <Content content={sections.hero.subtitle} />
          </Typography>
        </Stack>
      </Stack>
      <div
        className='z-0 brightness-20 absolute inset-0 h-full w-full'
        style={{
          backgroundImage: 'url("/images/backgrounds/dots-white.png")',
        }}
      />
    </PageSection>
  );
}

function PageSectionStory({ sections }: AboutPageSectionProps) {
  return (
    <PageSection disableGutters>
      <GlowCard border='top'>
        <Stack
          className='md:mt-12 [&_.quark-content-prose]:text-lg md:[&_.quark-content-prose]:text-xl'
          spacing={4}
          justifyContent='center'
          alignItems='center'
        >
          <Typography variant='h2'>
            <Content content={sections.story.title} />
          </Typography>
          <Content content={sections.story.content} styling='prose' />
        </Stack>
      </GlowCard>
    </PageSection>
  );
}

function PageSectionFounders({ sections }: AboutPageSectionProps) {
  return (
    <PageSection>
      <Stack
        spacing={{
          xs: 4,
          md: 8,
        }}
        justifyContent='center'
        alignItems='center'
      >
        <Stack spacing={1} justifyContent='center' alignItems='center'>
          <Typography variant='h2'>
            <Content content={sections.team.title} />
          </Typography>
          <Typography variant='subtitle1'>
            <Content content={sections.team.subtitle} />
          </Typography>
        </Stack>
        <Stack
          spacing={{
            xs: 8,
            md: 6,
          }}
        >
          {_.chain(sections.team.members)
            .sortBy('order', 'asc')
            .map((member) => <TeamMemberCard key={member.id} {...member} />)
            .value()}
        </Stack>
        <Typography>
          (and <strong>yes</strong> we are brothers)
        </Typography>
      </Stack>
    </PageSection>
  );
}

function PageSectionInvestors({ sections }: AboutPageSectionProps) {
  const computedInvestors = React.useMemo(() => {
    return sections.investors.people;
  }, [sections.investors.people]);

  return (
    <PageSection id='investors'>
      <Typography variant='h3' align='left' gutterBottom>
        <Content content={sections.investors.title} />
      </Typography>
      <Typography variant='subtitle1' align='left'>
        <Content content={sections.investors.subtitle} />
      </Typography>
      <div className='h-1 mt-16' />
      <Fade in={true} timeout={100}>
        {(transitionProps) => (
          <div {...transitionProps}>
            <Grid
              container
              spacing={{ xs: 0, sm: 2, md: 8 }}
              rowSpacing={{ xs: 4 }}
              className='justify-center items-center'
            >
              {computedInvestors.map((investor) => (
                <Grid
                  key={investor.id}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4,
                  }}
                >
                  <Stack direction='row' spacing={2} alignItems='center'>
                    <Avatar src={investor.avatar.src} />
                    <Typography className='text-md'>
                      <span className='font-bold'>{investor.name}</span>
                      <br />
                      <span className='text-gray-400'>{investor.title}</span>
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </div>
        )}
      </Fade>
    </PageSection>
  );
}

function PageSectionCareers({ sections }: AboutPageSectionProps) {
  return (
    <PageSection>
      <Stack spacing={4} justifyContent='center' alignItems='center'>
        <Stack spacing={1} justifyContent='center' alignItems='center' className='text-center'>
          <Typography variant='h2'>
            <Content content={sections.careers.title} />
          </Typography>
          <Typography variant='subtitle1'>
            <Content content={sections.careers.subtitle} />
          </Typography>
        </Stack>
        <div className='pb-4'>
          <ButtonLink
            startIcon={<TablerIconHeartHandshake />}
            variant='contained'
            size='xlarge'
            className='text-sm sm:text-md md:text-lg py-2 px-4 sm:py-3 sm:px-6'
            to='/careers'
          >
            {sections.careers.cta}
          </ButtonLink>
        </div>
      </Stack>
    </PageSection>
  );
}

/*
|------------------
| Internal
|------------------
*/

type AboutPageSectionProps<T extends object = object> = TF.Merge<
  T,
  {
    sections: LoadResult['sections'];
  }
>;

type TeamMember = LoadResult['sections']['team']['members'][number];

type TeamMemberCardProps = TeamMember;

function TeamMemberCard(props: TeamMemberCardProps) {
  const [showCartoon, setShowCartoon] = React.useState(false);
  return (
    <Stack
      spacing={6}
      className='w-full'
      direction={{
        xs: 'column',
        md: 'row',
      }}
      alignItems={{
        xs: 'center',
        md: 'end',
      }}
    >
      <div className='min-w-[248px] min-h-[248px] cursor-duck'>
        {showCartoon ? (
          <Img
            onMouseEnter={() => setShowCartoon(true)}
            onMouseLeave={() => setShowCartoon(false)}
            location='static'
            src={props.cartoon.src}
            height={props.cartoon.height}
            width={props.cartoon.width}
            responsive
            className='max-w-[248px] rounded'
            alt={`Image of ${props.familyName} ${props.givenName}`}
          />
        ) : (
          <Img
            onMouseEnter={() => setShowCartoon(true)}
            onMouseLeave={() => setShowCartoon(false)}
            location='static'
            src={props.avatar.src}
            height={props.avatar.height}
            width={props.avatar.width}
            responsive
            className='max-w-[248px] rounded'
            alt={`Image of ${props.familyName} ${props.givenName}`}
          />
        )}
      </div>
      <Stack>
        <Typography variant='h5' component='h5'>
          {props.givenName} {props.familyName}
        </Typography>
        <Typography variant='subtitle2' component='p'>
          {props.role}
        </Typography>
        <Content content={props.bio} />
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ xs: 'start', md: 'center' }}
          className='mt-4'
          divider={<Divider orientation='vertical' flexItem />}
        >
          <Stack direction='row' spacing={0.25} alignItems='center' className='w-fit md:pr-3'>
            <div className='md:hidden block'>contact:</div>
            <IconButton<'a'>
              component='a'
              href={props.links.github}
              target='_blank'
              rel='noopener noreferrer'
              variant='text'
              aria-label='GitHub'
              className='hover:bg-primary p-1 rounded-sm'
            >
              <TablerIconBrandGitHub />
            </IconButton>
            <IconButton<'a'>
              component='a'
              href={props.links.linkedin}
              target='_blank'
              rel='noopener noreferrer'
              variant='text'
              aria-label='LinkedIn'
              className='hover:bg-primary p-1 rounded-sm'
            >
              <TablerIconBrandLinkedin />
            </IconButton>
            <Link color='inherit' href={`mailto:${props.email}`} className='ml-2'>
              {props.email}
            </Link>
          </Stack>
          <Stack direction='row' spacing={1} className='md:pl-3' alignItems='center'>
            <div>techstack:</div>
            {props.techstack.map((tech) => (
              <Stack
                key={tech.name}
                direction='row'
                spacing={0.5}
                alignItems='center'
                className='w-fit'
              >
                <Img
                  location='static'
                  className='w-auto h-7'
                  src={tech.icon.src}
                  height={tech.icon.height}
                  width={tech.icon.width}
                  alt={tech.name}
                />
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
